const CALLBACK_KEY = 'm365_watchlist';
const SAFE_RESULTS = new Set(['authorized', 'error']);
const AUTHORIZATION_KEYS = Object.freeze(['code', 'state']);
const AUTHORIZATION_VALUE_MAX_LENGTH = 8192;
const BROWSER_SESSION_MAX_LENGTH = 128;
const BROWSER_SESSION_KEY = 'alphamizer.watchlistM365.browserSession';

let heldAuthorization = null;

export function readWatchlistM365CallbackResult(search) {
    const params = new URLSearchParams(String(search || ''));
    const entries = Array.from(params.entries());
    if (entries.length !== 1 || entries[0][0] !== CALLBACK_KEY) {
        return null;
    }
    return SAFE_RESULTS.has(entries[0][1]) ? entries[0][1] : null;
}

// Microsoft's one-time code and state come back in the URL fragment, which the
// browser never sends to a server. Accept exactly those two values.
function readAuthorizationResponse(hash) {
    const params = new URLSearchParams(String(hash || '').replace(/^#/, ''));
    const keys = Array.from(params.keys());
    if (
        keys.length !== AUTHORIZATION_KEYS.length
        || !AUTHORIZATION_KEYS.every((key) => keys.includes(key))
    ) {
        return null;
    }
    const response = { code: params.get('code'), state: params.get('state') };
    return Object.values(response).every(
        (value) => value && value.length <= AUTHORIZATION_VALUE_MAX_LENGTH,
    ) ? response : null;
}

export function safeWatchlistM365SettingsReturn(search) {
    const result = readWatchlistM365CallbackResult(search);
    return result ? `?${CALLBACK_KEY}=${result}` : '';
}

export function removeWatchlistM365CallbackResult(search) {
    const params = new URLSearchParams(String(search || ''));
    params.delete(CALLBACK_KEY);
    const next = params.toString();
    return next ? `?${next}` : '';
}

// The tab that starts Connect keeps the one-time browser binding, so only that
// tab can finish the connection when Microsoft sends it back.
export function rememberWatchlistM365BrowserSession(value) {
    if (
        typeof value !== 'string'
        || !value
        || value.length > BROWSER_SESSION_MAX_LENGTH
    ) {
        return false;
    }
    try {
        window.sessionStorage.setItem(BROWSER_SESSION_KEY, value);
        return true;
    } catch (_error) {
        return false;
    }
}

function takeBrowserSession() {
    try {
        const value = window.sessionStorage.getItem(BROWSER_SESSION_KEY) || '';
        window.sessionStorage.removeItem(BROWSER_SESSION_KEY);
        return value;
    } catch (_error) {
        return '';
    }
}

// Runs once at start-up, before sign-in shows anything: Microsoft's code and
// state leave the address bar at once, and every return from Microsoft, even a
// cancelled one, uses up this tab's browser binding.
export function captureWatchlistM365Return() {
    const { hash, pathname, search } = window.location;
    const result = readWatchlistM365CallbackResult(search);
    if (!result) {
        return;
    }
    const response = result === 'authorized' ? readAuthorizationResponse(hash) : null;
    const browserSession = takeBrowserSession();
    heldAuthorization = response && browserSession
        ? { ...response, browser_session: browserSession }
        : null;
    if (hash) {
        window.history.replaceState(window.history.state, '', `${pathname}${search}`);
    }
}

// Hands out the completion request once; a second caller gets null.
export function takeWatchlistM365Authorization() {
    const authorization = heldAuthorization;
    heldAuthorization = null;
    return authorization;
}
